import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLinkCollectionProject, useProjects } from "~/api/queries";
import type { Collection } from "~/api/types";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";

/**
 * "Move to project…" from the collection menu — picks a project for a collection. The
 * collection's current project (if any) is disabled and suffixed "(current)".
 */
export function MoveToProjectDialog({
  collection,
  onClose,
}: {
  collection: Collection;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: projects } = useProjects();
  const linkProject = useLinkCollectionProject();
  const [projectId, setProjectId] = useState(collection.projectId ?? "");

  const submit = () => {
    linkProject.mutate(
      { collectionId: collection.id, projectId: projectId || null },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("Collection moved"));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Move to project")}
      description={collection.name}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={linkProject.isPending} onClick={submit}>
            {t("Move")}
          </Button>
        </>
      }
    >
      <Select
        label={t("Project")}
        fullWidth
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
      >
        <option value="">{t("No project")}</option>
        {(projects ?? []).map((project) => (
          <option
            key={project.id}
            value={project.id}
            disabled={project.id === collection.projectId}
          >
            {project.name}
            {project.id === collection.projectId ? ` (${t("current")})` : ""}
          </option>
        ))}
      </Select>
    </Dialog>
  );
}
